import { Box } from '@mui/material';

import { PageContainer } from '@toolpad/core/PageContainer';
import type React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArchiveTenderDialog from '../components/Dialogs/ArchiveTenderDialog';
import ConfirmationDialog from '../components/Dialogs/ConfirmationDialog';
import AdHocDocGenerationStep from '../components/TenderDetails/AdHocDocGenerationStep';
import CapabilityAnalysisStep from '../components/TenderDetails/CapabilityAnalysisStep';
import ConfigurationStep from '../components/TenderDetails/ConfigurationStep';
import CreateDocxStep from '../components/TenderDetails/CreateDocxStep';
import DraftGenerationStep from '../components/TenderDetails/DraftGenerationStep';
import GenerateCVsStep from '../components/TenderDetails/GenerateCVsStep';
import ManageDocumentsStep from '../components/TenderDetails/ManageDocumentsStep';
import ManageTeamStep from '../components/TenderDetails/ManageTeamStep';
import ReviewAndEditStep from '../components/TenderDetails/ReviewAndEditStep';
import ReviewDraftsStep from '../components/TenderDetails/ReviewDraftsStep';
import StructureAndOutlineStep from '../components/TenderDetails/StructureAndOutlineStep';
import TenderContextStep from '../components/TenderDetails/TenderContextStep';
import TenderGoNoGoDecision from '../components/TenderDetails/TenderGoNoGoDecision';
import TenderHeader from '../components/TenderDetails/TenderHeader';
import TenderInquiryStep from '../components/TenderDetails/TenderInquiryStep';
import ThumbnailSidebar from '../components/TenderDetails/ThumbnailSidebar';
import UploadPartnerDocumentsStep from '../components/TenderDetails/UploadPartnerDocumentsStep';
import VisualPresentationsStep from '../components/TenderDetails/VisualPresentationsStep';
import { useSnackbar } from '../contexts/SnackbarContext';
import { TenderDetailsProvider, useTenderDetails } from '../contexts/TenderDetailsContext';
import { apiService } from '../services/api';
import { calculateStepStatuses } from '../utils/stepStatusHelper';

const TenderDetailsLayout: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  const {
    tender,
    validTenderId,
    isPartnerView,
    hasAddedContextFiles,
    forceEnablePursue,
    setForceEnablePursue,
    isArchiveDialogOpen,
    setArchiveDialogOpen,
    handleArchiveTender,
    isPursueDialogOpen,
    setPursueDialogOpen,
    isPursuingTender,
    setIsPursuingTender,
    pricingSettings,
    linkPartnerDialog,
    setLinkPartnerDialog,
    confirmLinkPartner,
    isLinking,
    stepCompletion,
    setOpenStep,
    user_docs,
    sectionsCount,
    reviewedSectionsCount,
    tenderForms,
    generatedCVs,
    reviewStepComplete,
  } = useTenderDetails();

  const handleScrollToStep = (stepId: number, elementId: string) => {
    setOpenStep(stepId);
    setTimeout(() => {
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 350); // wait for MUI accordion expand/collapse transitions (300ms) to finish so height is stable
  };

  const baseSteps = [
    { id: 1, label: 'Context', elementId: 'workflow-step-1' },
    { id: 2, label: 'Inquiry', elementId: 'workflow-step-2' },
    { id: 3, label: 'Capability', elementId: 'workflow-step-3' },
  ];

  const pursuingSteps = [
    ...baseSteps,
    { id: 4, label: 'Team', elementId: 'workflow-step-4' },
    { id: 5, label: 'Docs', elementId: 'workflow-step-5' },
    { id: 6, label: 'Config', elementId: 'workflow-step-6' },
    { id: 7, label: 'Strategy', elementId: 'workflow-step-7' },
    { id: 8, label: 'Generate', elementId: 'workflow-step-8' },
    { id: 9, label: 'Refine', elementId: 'workflow-step-9' },
    { id: 10, label: 'DOCX', elementId: 'workflow-step-10' },
    { id: 11, label: 'Visuals', elementId: 'workflow-step-11' },
    { id: 12, label: 'Fill', elementId: 'workflow-step-12' },
    { id: 13, label: 'CVs', elementId: 'workflow-step-13' },
    { id: 14, label: 'Review', elementId: 'workflow-step-14' },
  ];

  const activeSteps = tender.pursuitStatus === 'watching' ? baseSteps : pursuingSteps;

  const stepStatuses = calculateStepStatuses(
    tender,
    user_docs,
    stepCompletion || {},
    sectionsCount,
    reviewedSectionsCount,
    tenderForms || [],
    generatedCVs || [],
    reviewStepComplete
  );

  return (
    <PageContainer title="" maxWidth={false}>
      <Box sx={{ width: '100%', mx: 'auto', p: { xs: 2, md: 3 }, pt: 0, mt: -3 }}>
        <TenderHeader
          tender={tender}
          isPartnerView={!!isPartnerView}
          onBackClick={() =>
            navigate('/dashboard', {
              state: { tab: tender?.pursuitStatus === 'watching' ? 'watching' : 'pursuing' },
            })
          }
        />

        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <ThumbnailSidebar
            steps={activeSteps}
            stepStatuses={stepStatuses}
            onStepClick={handleScrollToStep}
          />

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <TenderContextStep />
            <TenderInquiryStep />
            <CapabilityAnalysisStep />

            {tender.pursuitStatus === 'watching' ? (
              <TenderGoNoGoDecision
                tender={tender}
                hasAddedContextFiles={hasAddedContextFiles}
                forceEnablePursue={forceEnablePursue}
                onSetForceEnablePursue={setForceEnablePursue}
                onArchive={() => setArchiveDialogOpen(true)}
                onPursue={() => setPursueDialogOpen(true)}
              />
            ) : (
              <>
                <UploadPartnerDocumentsStep />
                <ManageTeamStep />
                <ManageDocumentsStep />
                <ConfigurationStep />
                <StructureAndOutlineStep />
                <DraftGenerationStep />
                <ReviewAndEditStep />
                <CreateDocxStep />
                <VisualPresentationsStep />
                <AdHocDocGenerationStep />
                <GenerateCVsStep />
                <ReviewDraftsStep />
              </>
            )}
          </Box>
        </Box>
      </Box>

      <ArchiveTenderDialog
        open={isArchiveDialogOpen}
        onClose={() => setArchiveDialogOpen(false)}
        onConfirm={handleArchiveTender}
        tenderTitle={tender.title}
        tenderId={tender.id}
      />

      <ConfirmationDialog
        open={linkPartnerDialog.open}
        onClose={() => setLinkPartnerDialog({ open: false, partner: null })}
        onConfirm={confirmLinkPartner}
        isLoading={isLinking}
        title="Link Partner to Tender"
        contentText={`Are you sure you want to securely link ${linkPartnerDialog.partner?.companyName} to this tender? They will be granted view access to the tender and its library.`}
        confirmText="Confirm Link"
        cancelText="Cancel"
        sx={{
          backdropFilter: 'blur(3px)',
        }}
      />

      <ConfirmationDialog
        open={isPursueDialogOpen}
        onClose={() => setPursueDialogOpen(false)}
        onConfirm={async () => {
          setIsPursuingTender(true);
          try {
            const res = await apiService.pursueTender(validTenderId);
            if (res.success) {
              showSnackbar('Tender is now being pursued!', 'success');
              setPursueDialogOpen(false);
            } else {
              showSnackbar(res.message || 'Failed to pursue tender.', 'error');
            }
          } catch (error: unknown) {
            const err = error as Error;
            showSnackbar(err.message || 'Failed to pursue tender.', 'error');
          } finally {
            setIsPursuingTender(false);
          }
        }}
        isLoading={isPursuingTender}
        title="Pursue Tender"
        contentText={`You are about to make a Go decision for this tender. This action will incur a fee of ${pricingSettings?.workspace_init_fee_credits ?? 0} credits to unlock advanced features. Do you want to proceed?`}
        confirmText="Confirm Go (Pursue)"
        cancelText="Cancel"
        sx={{
          backdropFilter: 'blur(3px)',
        }}
      />
    </PageContainer>
  );
};

const TenderDetailsPage: React.FC = () => {
  const { tenderId } = useParams<{ tenderId: string }>();
  if (!tenderId) {
    return null;
  }
  return (
    <TenderDetailsProvider tenderId={tenderId}>
      <TenderDetailsLayout />
    </TenderDetailsProvider>
  );
};

export default TenderDetailsPage;
